import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ChevronLeft, ChevronRight, Search, Clock, User } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [search, setSearch] = useState("");

  useEffect(() => {
    axios.get(`${API}/blog-categories`).then(r => setCategories(["All", ...r.data])).catch(console.error);
  }, []);

  useEffect(() => {
    const params = { page, limit: 12 };
    if (activeCategory !== "All") params.category = activeCategory;
    axios.get(`${API}/blogs`, { params }).then(r => {
      setBlogs(r.data.blogs);
      setPages(r.data.pages);
      setTotal(r.data.total);
    }).catch(console.error);
  }, [page, activeCategory]);

  const filtered = search ? blogs.filter(b => b.title.toLowerCase().includes(search.toLowerCase())) : blogs;

  return (
    <div data-testid="blog-page">
      <section className="bg-gradient-to-b from-orange-50 to-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 mb-4 font-body text-xs uppercase tracking-wider">{total} Articles</Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-semibold text-slate-900 mb-4" data-testid="blog-title">Health & Travel Blog</h1>
          <p className="text-base md:text-lg text-slate-500 max-w-2xl mx-auto mb-8">Expert guides on medical tourism, treatments, wellness, and travel planning for your healthcare journey to India.</p>
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search articles..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 rounded-full" data-testid="blog-search" />
          </div>
        </div>
      </section>

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center mb-10" data-testid="blog-categories">
            {categories.map((c) => (
              <Button key={c} variant={activeCategory === c ? "default" : "outline"} size="sm"
                className={`rounded-full text-xs ${activeCategory === c ? "bg-orange-600 hover:bg-orange-700 text-white" : "border-slate-200 text-slate-600 hover:text-orange-700 hover:border-orange-200"}`}
                onClick={() => { setActiveCategory(c); setPage(1); }} data-testid={`blog-cat-${c}`}>
                {c}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((b) => (
              <Link to={`/blog/${b.slug}`} key={b.id}>
                <Card className="bg-white rounded-2xl border border-slate-100 overflow-hidden card-hover h-full" data-testid={`blog-card-${b.id}`}>
                  <div className="h-2 bg-gradient-to-r from-orange-500 to-emerald-300"></div>
                  <div className="p-6">
                    <Badge variant="outline" className="text-xs border-orange-200 text-orange-700 mb-3">{b.category}</Badge>
                    <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2 line-clamp-2">{b.title}</h3>
                    <p className="text-slate-500 text-sm leading-relaxed mb-4 line-clamp-3">{b.excerpt}</p>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {b.read_time}</span>
                      <span className="flex items-center gap-1"><User className="h-3 w-3" /> {b.author}</span>
                    </div>
                  </div>
                </Card>
              </Link>
            ))}
          </div>

          {pages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12" data-testid="blog-pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="rounded-full">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
                const p = i + 1;
                return (
                  <Button key={p} variant={page === p ? "default" : "outline"} size="sm" onClick={() => setPage(p)}
                    className={`rounded-full w-9 h-9 ${page === p ? "bg-orange-600 text-white" : ""}`}>
                    {p}
                  </Button>
                );
              })}
              {pages > 7 && <span className="text-slate-400">...</span>}
              <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="rounded-full">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
